import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import {
  BadgeCheck,
  CalendarClock,
  CircleDollarSign,
  ShieldCheck,
  ShoppingBag,
  Video,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BRAND_NAME, getPageTitle } from '@/lib/branding'

export function AboutPage() {
  return (
    <>
      <Helmet>
        <title>{getPageTitle('About')}</title>
        <meta
          name="description"
          content={`Learn how ${BRAND_NAME} works: one featured weekly deal, always-available past reviews, and transparent gear recommendations for video creators.`}
        />
      </Helmet>

      <div className="flex flex-1 flex-col bg-zinc-50">
        <section className="relative overflow-hidden border-b border-zinc-200 py-14 md:py-16">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-zinc-100 to-transparent" />
            <div className="absolute -left-24 top-10 h-56 w-56 rounded-full bg-red-100/50 blur-3xl" />
            <div className="absolute -right-24 bottom-10 h-56 w-56 rounded-full bg-zinc-200/70 blur-3xl" />
          </div>

          <div className="container relative mx-auto px-4 text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-zinc-300/90 bg-white/90 px-4 py-1.5 text-sm font-medium text-zinc-700">
              <BadgeCheck className="size-4" />
              About {BRAND_NAME}
            </span>
            <h1 className="mx-auto mb-4 max-w-4xl text-4xl font-bold text-zinc-900 md:text-5xl">
              Trusted Gear Reviews With Real Weekly Deals
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-zinc-600">
              We&apos;re video creators building a simple buying model: one featured
              weekly deal, plus a growing collection of past reviews that stay
              available at discounted pricing.
            </p>
          </div>
        </section>

        <section className="container mx-auto w-full px-4 py-12 md:py-14">
          <div className="mx-auto grid max-w-5xl gap-10">
            <div className="grid gap-6 md:grid-cols-2">
              <article className="rounded-2xl border border-zinc-200 bg-white p-6">
                <h2 className="mb-3 text-xl font-semibold text-zinc-900">
                  Our Mission
                </h2>
                <p className="text-zinc-600">
                  Great production gear shouldn&apos;t require guessing, hype, or
                  inflated pricing. We test and review equipment, then negotiate
                  meaningful discounts with manufacturers for products we&apos;d use
                  ourselves.
                </p>
              </article>

              <article className="rounded-2xl border border-zinc-200 bg-white p-6">
                <h2 className="mb-3 text-xl font-semibold text-zinc-900">
                  Built for Clarity
                </h2>
                <p className="text-zinc-600">
                  Every product page combines the review video, pricing, and key
                  details in one place. You can compare options confidently, then
                  check out securely through Stripe in minutes.
                </p>
              </article>
            </div>

            <article className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8">
              <h2 className="mb-6 text-2xl font-semibold text-zinc-900">
                How It Works
              </h2>

              <div className="grid gap-5 md:grid-cols-3">
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
                  <div className="mb-3 inline-flex rounded-lg bg-red-100 p-2 text-red-700">
                    <CalendarClock className="size-5" />
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-zinc-900">
                    One Featured Deal Per Week
                  </h3>
                  <p className="text-sm text-zinc-600">
                    One product is promoted at our highest weekly discount for a
                    limited window.
                  </p>
                </div>

                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
                  <div className="mb-3 inline-flex rounded-lg bg-red-100 p-2 text-red-700">
                    <CircleDollarSign className="size-5" />
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-zinc-900">
                    Past Reviews Stay Discounted
                  </h3>
                  <p className="text-sm text-zinc-600">
                    Missed a feature week? Past reviews remain available with
                    ongoing savings.
                  </p>
                </div>

                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
                  <div className="mb-3 inline-flex rounded-lg bg-red-100 p-2 text-red-700">
                    <Video className="size-5" />
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-zinc-900">
                    Watch First, Buy Second
                  </h3>
                  <p className="text-sm text-zinc-600">
                    Every listing is tied to a hands-on review so you can evaluate
                    before purchasing.
                  </p>
                </div>
              </div>
            </article>

            <article className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8">
              <h2 className="mb-6 text-2xl font-semibold text-zinc-900">
                Why People Trust {BRAND_NAME}
              </h2>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                  <ShieldCheck className="mt-0.5 size-5 text-red-600" />
                  <p className="text-sm text-zinc-700">
                    Recommendations are based on real testing and creator-first
                    standards.
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                  <ShoppingBag className="mt-0.5 size-5 text-red-600" />
                  <p className="text-sm text-zinc-700">
                    Multi-product checkout lets you combine this week&apos;s deal
                    with past reviews in one order.
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                  <BadgeCheck className="mt-0.5 size-5 text-red-600" />
                  <p className="text-sm text-zinc-700">
                    Discounts and product details are consistent across the site,
                    from homepage feature to past reviews.
                  </p>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                  <ShieldCheck className="mt-0.5 size-5 text-red-600" />
                  <p className="text-sm text-zinc-700">
                    Secure Stripe checkout and order tracking support a clean
                    purchase experience.
                  </p>
                </div>
              </div>
            </article>

            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-zinc-200 bg-white p-6 text-center sm:flex-row">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/">View This Week&apos;s Deal</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
                <Link to="/past-reviews">Browse Past Reviews</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
